/**
 * CopyModuleDialog - Wizard de 2 pasos para copiar un módulo de otro curso
 * (TAN-10190). Paso 1: curso origen (pestañas Grupos/Programas); paso 2: módulo.
 *
 * La inserción en la estructura del programa la gobierna el diálogo padre vía
 * `onConfirmCopy` (TAN-10191). Los cursos y módulos de origen vienen de la API
 * real (TAN-10192), con sus estados de carga, error y vacío.
 */

import type { CopyModuleDialogProps } from 'app/types/modules';
import clsx from 'clsx';
import HeaderTabs, { panelElementId, tabElementId } from 'core/modules/components/tabs/HeaderTabs/HeaderTabs';
import React from 'react';
import AssetImage from '../../../../../atoms/AssetImage/AssetImage';
import Loading from '../../../../../atoms/Loading/Loading';
import SelectCustomizable from '../../../../../atoms/SelectCustomizable/SelectCustomizable';
import RadioGroup from '../../../../../molecules/RadioGroup/RadioGroup';
import Dialog from '../../../../../organisms/dialogs/Dialog/Dialog';
import CourseOptionRow from './CourseOptionRow';
import ModuleOptionRow from './ModuleOptionRow';
import useCopyModuleDialog, { COURSE_RADIO_NAME, MODULE_RADIO_NAME } from './useCopyModuleDialog';

function CopyModuleDialog({ isOpen, onClose, onConfirmCopy, programGuid }: CopyModuleDialogProps): JSX.Element {
  const {
    activeTab,
    courseGroups,
    dialogId,
    handleChangeSortBy,
    handleChangeTab,
    handleClose,
    handlePrimaryAction,
    handleSelectCourse,
    handleSelectModule,
    hasCoursesError,
    hasModulesError,
    isCopying,
    isLoadingCourses,
    isLoadingModules,
    isPrimaryActionDisabled,
    isStepSelectCourse,
    menuPortalTarget,
    moduleOptions,
    primaryButtonText,
    selectedCourse,
    selectedCourseGuid,
    selectedModuleGuid,
    shouldShowCourseGroup,
    shouldShowCourses,
    shouldShowCoursesEmpty,
    shouldShowModules,
    shouldShowModulesEmpty,
    shouldShowSortBy,
    sortByLabel,
    sortByOptions,
    sortByValue,
    stepDescriptionId,
    stepRef,
    stepSubtitle,
    t,
    tablistLabel,
    tabs,
    title,
  } = useCopyModuleDialog({ isOpen, onClose, onConfirmCopy, programGuid });

  return (
    <Dialog
      buttonTextCancel={t('actions:Cancel')}
      buttonTextSuccess={primaryButtonText}
      className="copy-module-dialog"
      id={dialogId}
      isCloseButtonHidden
      isDisabledSuccess={isPrimaryActionDisabled}
      isLoading={isCopying}
      isOpen={isOpen}
      onCancel={handleClose}
      onClose={handleClose}
      onSuccess={handlePrimaryAction}
      size="xl"
      text={stepSubtitle}
      title={title}
    >
      {/* Recibe el foco al cambiar de paso; su nombre anuncia en cuál está. */}
      <fieldset aria-labelledby={stepDescriptionId} className="copy-module-dialog__step" ref={stepRef} tabIndex={-1}>
        {isStepSelectCourse && (
          <>
            <div className="copy-module-dialog__toolbar">
              <HeaderTabs label={tablistLabel} onChangeTab={handleChangeTab} tabActive={activeTab} tabs={tabs} typeView="pill" />
              {shouldShowSortBy && (
                /* El label lo emite `SelectCustomizable`, asociado al control. */
                <div className="copy-module-dialog__sort">
                  <SelectCustomizable
                    label={sortByLabel}
                    menuPortalTarget={menuPortalTarget}
                    onChange={handleChangeSortBy}
                    options={sortByOptions}
                    size="small"
                    value={sortByValue}
                  />
                </div>
              )}
            </div>
            {/* Sin cursos, focusable: si no, el destino de `aria-controls` no se alcanza. */}
            <div
              aria-labelledby={tabElementId(activeTab)}
              className="copy-module-dialog__panel"
              id={panelElementId(activeTab)}
              role="tabpanel"
              tabIndex={shouldShowCourses ? undefined : 0}
            >
              {/* `Loading` se posiciona sobre su contenedor: necesita uno con alto propio. */}
              {isLoadingCourses && (
                <div className="copy-module-dialog__loading">
                  <Loading hasBackdrop={false} isFullScreen={false} />
                </div>
              )}
              {hasCoursesError && (
                <p className="copy-module-dialog__error" role="alert">
                  {t('organize-content:Could not load courses')}
                </p>
              )}
              {shouldShowCourses && (
                <ul className="copy-module-dialog__groups">
                  {courseGroups.map((group) => (
                    <li className="copy-module-dialog__group" key={group.id}>
                      <RadioGroup className="copy-module-dialog__radio-group" legend={group.title}>
                        <ul className="copy-module-dialog__options copy-module-dialog__options--cards">
                          {group.courses.map((course) => (
                            <li
                              className={clsx('copy-module-dialog__option-item', {
                                'copy-module-dialog__option-item--selected': course.guid === selectedCourseGuid,
                              })}
                              key={course.guid}
                            >
                              <CourseOptionRow
                                course={course}
                                isSelected={course.guid === selectedCourseGuid}
                                name={COURSE_RADIO_NAME}
                                onSelect={handleSelectCourse}
                                shouldShowGroup={shouldShowCourseGroup}
                              />
                            </li>
                          ))}
                        </ul>
                      </RadioGroup>
                    </li>
                  ))}
                </ul>
              )}
              {shouldShowCoursesEmpty && <p className="copy-module-dialog__empty">{t('organize-content:No courses available')}</p>}
            </div>
          </>
        )}

        {!isStepSelectCourse && selectedCourse && (
          <>
            <div className="copy-module-dialog__course">
              <span className="copy-module-dialog__course-image">
                <AssetImage alt="" id="noPictureProgram" src={selectedCourse.picture} />
              </span>
              <span className="copy-module-dialog__course-name">
                {selectedCourse.name}
                {selectedCourse.school_group_name && <span className="copy-module-dialog__course-group">{` | ${selectedCourse.school_group_name}`}</span>}
              </span>
            </div>

            {isLoadingModules && (
              <div className="copy-module-dialog__loading">
                <Loading hasBackdrop={false} isFullScreen={false} />
              </div>
            )}
            {hasModulesError && (
              <p className="copy-module-dialog__error" role="alert">
                {t('organize-content:Could not load modules')}
              </p>
            )}

            {/* La leyenda nombra el grupo de radios con el curso elegido, que ya
                se ve encima: se oculta para no repetirlo en pantalla. */}
            {shouldShowModules && (
              <RadioGroup className="copy-module-dialog__radio-group" isLegendHidden legend={selectedCourse.name}>
                <ul className="copy-module-dialog__options copy-module-dialog__options--divided">
                  {moduleOptions.map((module) => (
                    <li className="copy-module-dialog__option-item" key={module.guid}>
                      <ModuleOptionRow
                        authorLabel={t('course:Created by')}
                        isSelected={module.guid === selectedModuleGuid}
                        module={module}
                        name={MODULE_RADIO_NAME}
                        onSelect={handleSelectModule}
                      />
                    </li>
                  ))}
                </ul>
              </RadioGroup>
            )}
            {shouldShowModulesEmpty && <p className="copy-module-dialog__empty">{t('organize-content:No modules available')}</p>}
          </>
        )}
      </fieldset>
    </Dialog>
  );
}

export default CopyModuleDialog;
